/**
 * A virtualised list of view rows (day headers, blocks, ghosts) over one or more roots.
 * Rows sit in normal flow inside one translated wrapper, so a row that grows while typing pushes
 * the rows below without React work; the row being edited stays mounted when scrolled out of
 * range (rendered at its measured offset), so its textarea never loses focus (spike A).
 */
import { useVirtualizer } from '@tanstack/react-virtual'
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { ViewRow } from '../client/store.ts'
import { useStore, useUi } from './hooks.ts'
import { navigate } from './router.ts'
import { BlockRow, DayHeader, GhostRow } from './Row.tsx'

const NONE: ReadonlySet<string> = new Set()

export function Outline({
  rows,
  onOpenDay,
  onNearEnd,
  scrollToKey,
  renderExtra,
  viewKey = 'main',
  highlight,
  onTopRow,
}: {
  rows: readonly ViewRow[]
  onOpenDay?: (date: string) => void
  onNearEnd?: () => void
  scrollToKey?: string | null
  /** View-specific rows (today's task strip, a page's head). */
  renderExtra?: (row: Extract<ViewRow, { type: 'extra' }>) => ReactNode
  /** Which view this is (main, a sidebar pane): ↑/↓ stay inside the view you work in. */
  viewKey?: string
  /** Land on this block: scroll it into the middle and flash it (SPEC §7.1). */
  highlight?: { readonly id: string; readonly seq: number } | null
  /** The first row on screen changed (the journal follows the day in view). */
  onTopRow?: (index: number) => void
}) {
  const store = useStore()
  useUi()
  const mode = store.mode
  const req = store.caretReq
  const scrollRef = useRef<HTMLDivElement>(null)
  const rowsRef = useRef(rows)
  rowsRef.current = rows

  // This view's rows drive ↑/↓ navigation and selection while it is the active view.
  useEffect(() => {
    store.views.set(viewKey, () => rowsRef.current)
    return () => {
      store.views.delete(viewKey)
    }
  }, [store, viewKey])

  const index = useMemo(() => {
    const m = new Map<string, number>()
    rows.forEach((r, i) => {
      m.set(r.key, i)
      if (r.type === 'block' || r.type === 'ghost') m.set(r.id, i)
    })
    return m
  }, [rows])

  const selected = mode.kind === 'select' ? new Set(store.selectedIds()) : NONE
  const activeId = mode.kind === 'edit' ? mode.id : null
  const activeIndex = activeId === null ? -1 : (index.get(activeId) ?? -1)

  const v = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) => (rows[i]!.type === 'day' ? 56 : rows[i]!.type === 'label' ? 30 : rows[i]!.type === 'extra' ? 40 : 26),
    overscan: 12,
    getItemKey: (i) => rows[i]!.key,
  })

  useLayoutEffect(() => {
    if (!req) return
    const i = index.get(req.id)
    if (i === undefined) return
    const items = v.getVirtualItems()
    const first = items[0]?.index ?? 0
    const last = items[items.length - 1]?.index ?? 0
    if (i <= first + 1 || i >= last - 1) v.scrollToIndex(i, { align: 'auto' })
  }, [req?.seq]) // eslint-disable-line

  useLayoutEffect(() => {
    if (!scrollToKey) return
    const i = index.get(scrollToKey)
    if (i !== undefined) v.scrollToIndex(i, { align: 'start' })
  }, [scrollToKey]) // eslint-disable-line

  // Landing on a block: once its row exists, scroll it to the middle and flash it.
  const [flash, setFlash] = useState<string | null>(null)
  const landed = useRef(0)
  useLayoutEffect(() => {
    if (!highlight || landed.current === highlight.seq) return
    const i = index.get(highlight.id)
    if (i === undefined) return
    landed.current = highlight.seq
    v.scrollToIndex(i, { align: 'center' })
    setFlash(highlight.id)
    const t = setTimeout(() => setFlash(null), 1600)
    return () => clearTimeout(t)
  }, [highlight?.seq, highlight?.id, index]) // eslint-disable-line

  const items = v.getVirtualItems()
  const first = items[0]?.index ?? 0
  const last = items[items.length - 1]?.index ?? -1
  useEffect(() => {
    if (onNearEnd && last >= rows.length - 20) onNearEnd()
  }, [last, rows.length, onNearEnd])
  // The first row actually on screen (rendered items include overscan rows above it).
  const offset = v.scrollOffset ?? 0
  const top = items.find((it) => it.end > offset)?.index ?? first
  useEffect(() => {
    onTopRow?.(top)
  }, [top, onTopRow])
  const detached = activeIndex >= 0 && (activeIndex < first || activeIndex > last)

  const rowEl = (r: ViewRow) => {
    if (r.type === 'day') return <DayHeader date={r.date} onOpen={() => onOpenDay?.(r.date)} />
    if (r.type === 'label') return <LabelRow row={r} />
    if (r.type === 'extra') return renderExtra?.(r) ?? null
    if (r.type === 'ghost') {
      return (
        <GhostRow
          id={r.id}
          root={r.root}
          active={r.id === activeId}
          caretSeq={req && req.id === r.id ? req.seq : 0}
          label={r.date ? 'Write something…' : 'Empty. Click to write.'}
        />
      )
    }
    return (
      <BlockRow
        id={r.id}
        root={r.root}
        depth={r.depth}
        header={r.depth < 0}
        active={r.id === activeId}
        selected={selected.has(r.id)}
        hasKids={store.hasChildren(r.id)}
        caretSeq={req && req.id === r.id ? req.seq : 0}
      />
    )
  }

  return (
    <div
      ref={scrollRef}
      className="scroller"
      onMouseDownCapture={() => (store.activeView = viewKey)}
      onFocusCapture={() => (store.activeView = viewKey)}
      onMouseDown={(e) => {
        if (!(e.target as HTMLElement).closest('.row, .day, .label, .extra')) store.idle()
      }}
    >
      <div className="column">
        <div style={{ height: v.getTotalSize(), position: 'relative' }}>
          <div className="wrap" style={{ transform: `translateY(${items[0]?.start ?? 0}px)` }}>
            {items.map((vi) => {
              const r = rows[vi.index]!
              return (
                <div key={vi.key} data-index={vi.index} ref={v.measureElement} className={flash !== null && r.type === 'block' && r.id === flash ? 'flash' : undefined}>
                  {rowEl(r)}
                </div>
              )
            })}
          </div>
          {detached && (
            <div className="wrap" style={{ transform: `translateY(${v.measurementsCache[activeIndex]?.start ?? 0}px)` }}>
              {rowEl(rows[activeIndex]!)}
            </div>
          )}
        </div>
        <div className="tail" />
      </div>
    </div>
  )
}

function LabelRow({ row }: { row: Extract<ViewRow, { type: 'label' }> }) {
  const text = row.href ? (
    <a href={row.href} onClick={(e) => (e.preventDefault(), navigate(row.href!))}>
      {row.text}
    </a>
  ) : (
    row.text
  )
  if (row.onToggle) {
    return (
      <div className={`label ${row.level}`}>
        <button className="link toggle" aria-expanded={row.open} onClick={row.onToggle}>
          <span className="caret">{row.open ? '▾' : '▸'}</span> {row.text}
        </button>
      </div>
    )
  }
  return <div className={`label ${row.level}`}>{text}</div>
}
